"use client"

import { Button, Col, Popover, Row } from "antd"
import { CloseCircleFilled, MinusOutlined, PlusOutlined } from "@ant-design/icons"
import type { FC, ReactNode } from "react"
import { useTranslation } from "react-i18next"

export type VisaTravelerCount = {
  adult: number
  child: number
  infant: number
}

export const DEFAULT_TRAVELER_COUNT: VisaTravelerCount = { adult: 1, child: 0, infant: 0 }

type TravelerCountSelectProps = {
  value: VisaTravelerCount
  onChange: (value: VisaTravelerCount) => void
  /** Mặc định "large" để khớp DatePicker/Select size="large" ở box search. Đứng cạnh field
   * size mặc định (vd trong VisaInquiryForm) thì truyền "middle" để không bị lệch chiều cao. */
  size?: "middle" | "large"
}

type CounterRowProps = {
  label: string
  hint: string
  count: number
  min: number
  max: number
  onChange: (next: number) => void
}

// Mốc tuổi lấy đúng theo flight (InputAdult/InputChildren/InputInfant.tsx +
// i18n common.adult_rule/child_rule/infant_rule) để nhất quán trong toàn app.
const CounterRow = ({ label, hint, count, min, max, onChange }: CounterRowProps): ReactNode => (
  <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
    <Col>
      <div>{label}</div>
      <div style={{ fontSize: 12, color: "#8c8c8c" }}>{hint}</div>
    </Col>
    <Col>
      <Row gutter={8} align="middle" wrap={false}>
        <Col>
          <Button
            size="small"
            shape="circle"
            icon={<MinusOutlined />}
            disabled={count <= min}
            onClick={() => onChange(count - 1)}
          />
        </Col>
        <Col style={{ width: 24, textAlign: "center" }}>{count}</Col>
        <Col>
          <Button
            size="small"
            shape="circle"
            icon={<PlusOutlined />}
            disabled={count >= max}
            onClick={() => onChange(count + 1)}
          />
        </Col>
      </Row>
    </Col>
  </Row>
)

/** Popover +/- người lớn/trẻ em/em bé — cùng kiểu UI và mốc tuổi với ô chọn hành khách của
 * flight, nhưng state cục bộ riêng cho form search visa, không dùng chung redux flight. */
const TravelerCountSelect: FC<TravelerCountSelectProps> = ({ value, onChange, size = "large" }) => {
  const { t } = useTranslation()
  const total = value.adult + value.child + value.infant
  const isDefault =
    value.adult === DEFAULT_TRAVELER_COUNT.adult &&
    value.child === DEFAULT_TRAVELER_COUNT.child &&
    value.infant === DEFAULT_TRAVELER_COUNT.infant

  const content = (
    <div style={{ width: 260 }}>
      <CounterRow
        label={t("visa.traveler_count.adult_label")}
        hint={t("visa.traveler_count.adult_hint")}
        count={value.adult}
        min={1}
        max={20}
        onChange={(adult) => onChange({ ...value, adult })}
      />
      <CounterRow
        label={t("visa.traveler_count.child_label")}
        hint={t("visa.traveler_count.child_hint")}
        count={value.child}
        min={0}
        max={10}
        onChange={(child) => onChange({ ...value, child })}
      />
      <CounterRow
        label={t("visa.traveler_count.infant_label")}
        hint={t("visa.traveler_count.infant_hint")}
        count={value.infant}
        min={0}
        max={10}
        onChange={(infant) => onChange({ ...value, infant })}
      />
    </div>
  )

  return (
    <Popover trigger="click" content={content} placement="bottomLeft">
      <Button size={size} style={{ width: "100%", textAlign: "left" }}>
        <Row justify="space-between" align="middle" wrap={false}>
          <Col>{t("visa.traveler.total", { count: total })}</Col>
          {!isDefault && (
            <Col>
              <CloseCircleFilled
                style={{ color: "#bfbfbf" }}
                onClick={(e) => {
                  e.stopPropagation()
                  onChange(DEFAULT_TRAVELER_COUNT)
                }}
              />
            </Col>
          )}
        </Row>
      </Button>
    </Popover>
  )
}

export default TravelerCountSelect
